/**
 * Block renderers for the semantic summary engine.
 *
 * Each renderer takes a `TSemanticNode` and renders it with design-system
 * primitives. The registry at the bottom maps `TRendererKind` → component,
 * so new renderers plug in without touching the pipeline or the view.
 */
/* eslint-disable react-refresh/only-export-components --
 * components are consumed via the BLOCK_RENDERERS registry export */

import { Check, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { useState } from "react";
import ReactMarkdown from "react-markdown";

import type { TRendererKind, TSemanticNode } from "@/lib/semantic-summary";
import { formatProseParagraphs, humanizeEnum, sanitizeText, toDisplayString } from "@/lib/semantic-summary/format";
import { cn } from "@/lib/utils";

export type TBlockRendererProps = {
  node: TSemanticNode;
};

function BlockLabel({ text }: { text: string }) {
  return <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{text}</p>;
}

/* ------------------------------------------------------------------ */
/* Tag group — industries, stages, geographies, keywords               */
/* ------------------------------------------------------------------ */

function TagGroupBlock({ node }: TBlockRendererProps) {
  const tags = Array.isArray(node.value) ? (node.value as unknown[]).map(String) : [];
  if (tags.length === 0) return null;
  return (
    <div>
      <BlockLabel text={node.label} />
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium text-foreground"
          >
            {/^[A-Z][A-Z0-9_]+$/.test(tag) ? humanizeEnum(tag) : sanitizeText(tag)}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Expandable text — theses, descriptions, any long copy               */
/* ------------------------------------------------------------------ */

const CLAMP_THRESHOLD = 320;

function ExpandableTextBlock({ node }: TBlockRendererProps) {
  const text = formatProseParagraphs(sanitizeText(typeof node.value === "string" ? node.value : ""));
  const [expanded, setExpanded] = useState(false);
  const needsClamp = text.length > CLAMP_THRESHOLD;

  return (
    <div>
      <BlockLabel text={node.label} />
      <div
        className={cn(
          "markdown-content max-w-[75ch] text-sm leading-relaxed text-foreground [&_p:last-child]:mb-0 [&_p]:mb-3",
          needsClamp && !expanded && "line-clamp-4"
        )}
      >
        <ReactMarkdown>{text}</ReactMarkdown>
      </div>
      {needsClamp && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80"
        >
          {expanded ? (
            <>
              Show less <ChevronUp className="h-3 w-3" />
            </>
          ) : (
            <>
              Read more <ChevronDown className="h-3 w-3" />
            </>
          )}
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Checklist — must-haves, criteria, lists of longer strings           */
/* ------------------------------------------------------------------ */

const CHECKLIST_PREVIEW_COUNT = 5;

function ChecklistBlock({ node }: TBlockRendererProps) {
  const items = Array.isArray(node.value) ? (node.value as unknown[]).map(String) : [];
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return null;

  const visible = expanded ? items : items.slice(0, CHECKLIST_PREVIEW_COUNT);
  const hiddenCount = items.length - visible.length;

  return (
    <div>
      <BlockLabel text={node.label} />
      <ul className="max-w-[75ch] space-y-3">
        {visible.map((item) => (
          <li
            key={item}
            className="flex items-start gap-3 text-sm leading-relaxed text-foreground"
          >
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Check className="h-3 w-3 text-primary" />
            </span>
            <span>{sanitizeText(item)}</span>
          </li>
        ))}
      </ul>
      {(hiddenCount > 0 || expanded) && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80"
        >
          {expanded ? (
            <>
              Show less <ChevronUp className="h-3 w-3" />
            </>
          ) : (
            <>
              Show {hiddenCount} more <ChevronDown className="h-3 w-3" />
            </>
          )}
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Facts list — extracted truths, collapsed by default                 */
/* ------------------------------------------------------------------ */

function FactsListBlock({ node }: TBlockRendererProps) {
  const items = Array.isArray(node.value) ? (node.value as unknown[]).map(String) : [];
  const [open, setOpen] = useState(false);
  if (items.length === 0) return null;

  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="-mx-2 flex w-[calc(100%+1rem)] items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-muted/60"
      >
        <span className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{node.label}</span>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
            {items.length}
          </span>
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {open && (
        <ul className="mt-3 max-w-[75ch] space-y-2 border-l-2 border-primary/30 pl-4">
          {items.map((item) => (
            <li
              key={item}
              className="text-sm leading-relaxed text-muted-foreground"
            >
              {sanitizeText(item)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Key/value — enums, URLs, dates, booleans, loose numbers             */
/* ------------------------------------------------------------------ */

function KeyValueBlock({ node }: TBlockRendererProps) {
  const { value, dataType } = node;

  let content: React.ReactNode;
  if (dataType === "url" && typeof value === "string") {
    content = (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
      >
        {value.replace(/^https?:\/\//, "")}
        <ExternalLink className="h-3 w-3" />
      </a>
    );
  } else if (dataType === "email" && typeof value === "string") {
    content = (
      <a
        href={`mailto:${value}`}
        className="text-sm font-medium text-primary hover:underline"
      >
        {value}
      </a>
    );
  } else if (dataType === "date" && typeof value === "string") {
    const parsed = new Date(value);
    content = (
      <span className="text-sm text-foreground">
        {Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString()}
      </span>
    );
  } else if (dataType === "boolean") {
    content = <span className="text-sm text-foreground">{value ? "Yes" : "No"}</span>;
  } else if (dataType === "enum" && typeof value === "string") {
    content = <span className="text-sm text-foreground">{humanizeEnum(value)}</span>;
  } else {
    content = <span className="text-sm text-foreground">{sanitizeText(String(value))}</span>;
  }

  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs font-medium text-muted-foreground">{node.label}</span>
      {content}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Object cards — arrays of structured entities (portfolio, team, …)   */
/* ------------------------------------------------------------------ */

function ObjectCardsBlock({ node }: TBlockRendererProps) {
  const items = Array.isArray(node.value) ? (node.value as Record<string, unknown>[]) : [];
  if (items.length === 0) return null;

  return (
    <div>
      <BlockLabel text={`${node.label} (${items.length})`} />
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="rounded-xl border border-border bg-muted/40 p-3"
          >
            {Object.entries(item)
              .map(([k, v]) => [k, toDisplayString(v)] as const)
              .filter(([, v]) => v.trim() !== "")
              .slice(0, 6)
              .map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-baseline justify-between gap-3 py-0.5"
                >
                  <span className="text-xs text-muted-foreground">{k.replace(/_/g, " ")}</span>
                  <span className="truncate text-xs font-medium text-foreground">{sanitizeText(v)}</span>
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Registry                                                            */
/* ------------------------------------------------------------------ */

export const BLOCK_RENDERERS: Record<TRendererKind, React.ComponentType<TBlockRendererProps>> = {
  tagGroup: TagGroupBlock,
  expandableText: ExpandableTextBlock,
  checklist: ChecklistBlock,
  factsList: FactsListBlock,
  keyValue: KeyValueBlock,
  objectCards: ObjectCardsBlock,
  // Recommendations render through the dedicated callout row in the view.
  callouts: ExpandableTextBlock,
};
